import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import queryString from "query-string";
import { get } from "lodash";
import { useLocation } from "react-router-dom";
import SuccessRegistration from "@tvg/atomic-ui/_templates/SuccessRegistration";

import {
  capitalizeFirstLetter,
  replaceAll,
  isTvg5
} from "@tvg/utils/generalUtils";
import tvgConf from "@tvg/conf";
import {
  getPawsWalletAvailable,
  getRegistrationTexts,
  getSelectedPromo,
  getWelcomeFields
} from "../utils/selectors";
import {
  onSuccessAuthenticationGtmEvent,
  onButtonClickGtmEvent
} from "../utils/gtm/authentication";
import { onVirtualPageViewGtmEvent } from "../utils/gtm/pageViews";

const buildWalletUrl = () =>
  tvgConf().buildUrl({
    path: "wallet"
  });

const buildDepositUrl = () =>
  tvgConf().buildUrl({
    app: "dma",
    path: "deposit",
    wrapper: true,
    callback:
      typeof window !== "undefined" && get(window, "location.host")
        ? window.location.host
        : true,
    params: { fullPage: true, more: true }
  });

const buildDepositUrlDesktop = () => {
  try {
    const url = buildDepositUrl();
    const urlObj = new URL(url);
    return `${urlObj.origin}/my-funds/deposit`;
  } catch {
    return "my-funds/deposit";
  }
};

export const buildPrimaryUrl = (pawsWalletAvailable, device) => {
  if (pawsWalletAvailable) return isTvg5() ? "/wallet" : buildWalletUrl();

  return device === "mobile" ? buildDepositUrl() : buildDepositUrlDesktop();
};

const innerBodyContent = ({ labels, selectedPromo, hasPromo, hasRaf }) => {
  const noLabel = {};
  const labelsRaf = get(labels, "raf", noLabel);
  const labelsPromo = get(labels, "promo", noLabel);
  const labelsDefault = get(labels, "default", noLabel);
  const nextSteps = get(selectedPromo, "nextSteps")
    ? get(selectedPromo, "nextSteps")
    : get(selectedPromo, "description", "");
  const promoDescription = nextSteps
    .split("\n")
    .map((text) => text.replace("- ", ""));
  let bodyContent = {};
  let show = false;
  if (hasRaf) {
    show = labelsRaf.show;
    bodyContent = {
      innerTitle: labelsRaf.innerTitle,
      innerList: get(selectedPromo, "nextSteps")
        ? promoDescription
        : labelsRaf.messages
    };
  } else if (hasPromo) {
    show = labelsPromo.show;
    bodyContent = {
      innerTitle: labelsPromo.innerTitle,
      innerList: get(selectedPromo, "nextSteps")
        ? promoDescription
        : labelsPromo.messages
    };
  } else {
    show = labelsDefault.show;
    bodyContent = {
      innerTitle: labelsDefault.innerTitle,
      innerList: labelsDefault.messages
    };
  }
  return show ? bodyContent : noLabel;
};

export const SuccessComp = ({ device }) => {
  const location = useLocation();
  const registrationTexts = useSelector(getRegistrationTexts);
  const selectedPromo = useSelector(getSelectedPromo);
  const welcomeFields = useSelector(getWelcomeFields);

  const hasPromo =
    selectedPromo && !!selectedPromo.code && selectedPromo.code !== "raf";
  const hasRaf =
    selectedPromo && !!selectedPromo.code && selectedPromo.code === "raf";
  const initialRender = useRef(true);
  const [redirectUrlParam, setRedirectUrlParam] = useState("");
  const [buttonSecondaryType, setButtonSecondaryType] = useState("");
  const pawsWalletAvailable = useSelector(getPawsWalletAvailable);

  const params = useCallback(
    () => queryString.parse(location.search),
    [JSON.stringify(location.search)]
  );

  useEffect(() => {
    onVirtualPageViewGtmEvent(location, "registration-complete");
  }, []);

  useEffect(() => {
    let redirectUrl = get(params(), "redirect", "");
    const urlPattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2 }|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i"
    );

    try {
      if (redirectUrl) {
        redirectUrl = decodeURIComponent(redirectUrl);

        // Validate if it's a proper url
        if (urlPattern.test(redirectUrl) || redirectUrl.charAt(0) === "/") {
          setRedirectUrlParam(redirectUrl);
        }
      }
    } catch (e) {
      // Nothing to do on catch handle since this is not mandatory
    }
  }, [params]);

  useEffect(() => {
    if (device === "mobile" || isTvg5()) {
      setButtonSecondaryType("link");
    } else {
      setButtonSecondaryType("a");
    }
  }, [redirectUrlParam]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      const promoCode = get(selectedPromo, "code", "");
      let module;

      switch (promoCode) {
        case "":
          module = "Promo To Be Added";
          break;
        case "raf":
          module = "Referral";
          break;
        default:
          module = "Pre-Applied Promo";
      }

      onSuccessAuthenticationGtmEvent(module);
    }
  }, [initialRender.current, JSON.stringify(selectedPromo)]);

  const buttonSecondaryLabel = get(
    registrationTexts,
    "success.buttonSecondaryLabel",
    ""
  );

  const gtmModule = get(selectedPromo, "gtmModule", "");

  return (
    <SuccessRegistration
      device={device}
      promoCode={selectedPromo.code}
      hasPromo={hasPromo}
      hasRaf={hasRaf}
      brand={tvgConf().brand}
      headerTitle={replaceAll(
        get(registrationTexts, "success.headerTitle", ""),
        "@@@firstname@@@",
        capitalizeFirstLetter(welcomeFields.firstName)
      )}
      headerDescription={get(
        registrationTexts,
        "success.headerDescription",
        ""
      )}
      {...innerBodyContent({
        labels: get(registrationTexts, "success.info", {}),
        selectedPromo,
        hasPromo,
        hasRaf
      })}
      buttonPrimaryLabel={get(
        registrationTexts,
        "success.buttonPrimaryLabel",
        ""
      )}
      buttonPrimaryURL={buildPrimaryUrl(pawsWalletAvailable, device)}
      buttonSecondaryLabel={buttonSecondaryLabel}
      buttonSecondaryURL={redirectUrlParam || "/"}
      handleButtonSecondary={() => {
        onButtonClickGtmEvent(buttonSecondaryLabel, gtmModule);
      }}
      buttonSecondaryType={buttonSecondaryType}
      onButtonClickGtmEvent={onButtonClickGtmEvent}
      gtmModule={gtmModule}
    />
  );
};

SuccessComp.defaultProps = {
  device: "mobile",
  location: {
    search: ""
  }
};

export default SuccessComp;
